import { useEffect, useRef, useState } from "react";
import { acquireSocket, releaseSocket, SOCKET_EVENTS } from "../lib/socket";

type Options = {
  gameCode: string;
  /** Server events that make the data stale. */
  events?: string[];
  /**
   * Safety net only. Sockets get blocked by some corporate proxies, and a
   * scoreboard that silently stops updating during an event is worse than an
   * occasional request: so we poll *only while disconnected*, never
   * alongside a healthy socket.
   */
  fallbackMs?: number;
};

/**
 * Turns server pushes into a revision counter. Callers refetch whenever it
 * changes, which keeps this hook out of the business of knowing response
 * shapes: the public and admin scoreboards differ.
 */
export function useLiveRefresh({
  gameCode,
  events = [SOCKET_EVENTS.scoreboardUpdated],
  fallbackMs = 15000,
}: Options) {
  const [revision, setRevision] = useState(0);
  const [connected, setConnected] = useState(false);

  /* Keep the event list stable across renders without asking every caller to
     memoise the array they pass in. */
  const eventsKey = events.join(",");

  const pendingRef = useRef<number | null>(null);
  const bump = useRef(() => {
    /* A burst of solves should cost one refetch, not one per solve. */
    if (pendingRef.current !== null) return;
    pendingRef.current = window.setTimeout(() => {
      pendingRef.current = null;
      setRevision((n) => n + 1);
    }, 400);
  }).current;

  useEffect(() => {
    if (!gameCode) return;

    const socket = acquireSocket();
    const names = eventsKey ? eventsKey.split(",") : [];

    const join = () => socket.emit("game:join", gameCode);

    const onConnect = () => {
      setConnected(true);
      join();
      /* Anything that happened while we were away is missed, so resync. */
      setRevision((n) => n + 1);
    };
    const onDisconnect = () => setConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    for (const name of names) socket.on(name, bump);

    if (socket.connected) {
      setConnected(true);
      join();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      for (const name of names) socket.off(name, bump);
      if (pendingRef.current !== null) {
        window.clearTimeout(pendingRef.current);
        pendingRef.current = null;
      }
      releaseSocket();
    };
  }, [gameCode, eventsKey, bump]);

  /* Fallback polling, active only when the socket is down. */
  useEffect(() => {
    if (!gameCode || connected || !fallbackMs) return;
    const id = window.setInterval(() => setRevision((n) => n + 1), fallbackMs);
    return () => window.clearInterval(id);
  }, [gameCode, connected, fallbackMs]);

  return {
    revision,
    connected,
    refreshNow: () => setRevision((n) => n + 1),
  };
}
